/* global window */
// Shared checklist helpers for the editor and the staff screens. The API owns
// the real scheduling logic (services/checklistSchedule.js); these only
// describe it for display.

export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const WEEKDAYS_LONG = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday'
];

export const FREQUENCY_OPTIONS = [
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'On demand', value: 'onDemand' }
];

export const WEEK_OPTIONS = [
  { label: '1st', value: 1 },
  { label: '2nd', value: 2 },
  { label: '3rd', value: 3 },
  { label: '4th', value: 4 },
  { label: 'Last', value: 'last' }
];

export const ITEM_TYPE_OPTIONS = [
  { label: 'Tick', value: 'tick', hint: 'Yes / No / N/A' },
  { label: 'Reading', value: 'reading', hint: 'A number, checked against a range' },
  { label: 'Text', value: 'text', hint: 'A short written answer' }
];

export const DEFAULT_DEPARTMENTS = ['Food & Beverage', 'Housekeeping', 'Kitchen'];

const weekLabel = week =>
  (WEEK_OPTIONS.find(o => o.value === week) || WEEK_OPTIONS[0]).label;

const dayList = days =>
  (days || [])
    .slice()
    .sort()
    .map(d => WEEKDAYS[d])
    .join(', ');

// "Daily · due 07:00", "Daily · Lunch 12:30, Dinner 19:00", "On demand".
export const describeSchedule = schedule => {
  const s = schedule || {};
  if (s.frequency === 'onDemand') return 'On demand';

  let base = 'Daily';
  if (s.frequency === 'weekly') base = `Weekly · ${dayList(s.days) || 'no days set'}`;
  if (s.frequency === 'monthly') {
    base = `Monthly · ${weekLabel(s.week)} ${WEEKDAYS_LONG[(s.days || [])[0]] || 'day'}`;
  }

  const windows = (s.windows || []).filter(w => w.due);
  if (!windows.length) return base;
  if (windows.length === 1) return `${base} · due ${windows[0].due}`;
  return `${base} · ${windows.map(w => `${w.label || 'Due'} ${w.due}`).join(', ')}`;
};

// Item-level rotation: "Mondays only", "1st Monday of the month".
export const describeRule = when => {
  if (!when || !when.frequency || when.frequency === 'daily') return '';
  if (when.frequency === 'weekly') return `${dayList(when.days)} only`;
  if (when.frequency === 'monthly') {
    return `${weekLabel(when.week)} ${WEEKDAYS_LONG[(when.days || [])[0]] || ''} of the month`;
  }
  return '';
};

export const describeRange = item => {
  if (!item || item.type !== 'reading') return '';
  const unit = item.unit || '';
  const hasMin = item.min !== null && item.min !== undefined && item.min !== '';
  const hasMax = item.max !== null && item.max !== undefined && item.max !== '';
  if (hasMin && hasMax) return `${item.min}${unit} – ${item.max}${unit}`;
  if (hasMax) return `${item.strict ? 'Below' : 'At most'} ${item.max}${unit}`;
  if (hasMin) return `${item.strict ? 'Above' : 'At least'} ${item.min}${unit}`;
  return 'Any value';
};

export const countItems = template =>
  ((template && template.sections) || []).reduce(
    (n, s) => n + ((s.items || []).length || 0),
    0
  );

export const countPhotoItems = template =>
  ((template && template.sections) || []).reduce(
    (n, s) => n + (s.items || []).filter(i => i.photoRequired).length,
    0
  );

// Client-side ids so unsaved rows have stable React keys. The server assigns
// the real keys and ignores these.
let cidCounter = 0;
export const cid = () => `c${Date.now().toString(36)}${(cidCounter += 1)}`;

export const blankItem = (label = '') => ({
  _cid: cid(),
  label,
  standard: '',
  type: 'tick',
  photoRequired: false,
  critical: false,
  responsible: ''
});

export const blankSection = (title = '') => ({
  _cid: cid(),
  title,
  note: '',
  assignees: [],
  items: [blankItem()]
});

export const blankTemplate = (access, department = '') => ({
  access,
  name: '',
  department,
  description: '',
  active: true,
  requiresSignOff: true,
  allowMarkAll: true,
  assignees: [],
  schedule: {
    frequency: 'daily',
    days: [],
    week: 1,
    windows: [{ _cid: cid(), key: '', label: '', start: '06:00', due: '07:00' }]
  },
  runFields: [],
  sections: [blankSection()]
});

// Deep copy for editing, tagging rows with client ids.
export const toDraft = template => {
  const copy = JSON.parse(JSON.stringify(template));
  copy.schedule = copy.schedule || { frequency: 'daily', windows: [] };
  copy.schedule.windows = (copy.schedule.windows || []).map(w => ({ ...w, _cid: cid() }));
  copy.runFields = (copy.runFields || []).map(f => ({ ...f, _cid: cid() }));
  copy.sections = (copy.sections || []).map(s => ({
    ...s,
    _cid: cid(),
    items: (s.items || []).map(i => ({ ...i, _cid: cid() }))
  }));
  return copy;
};

// Props that make a card-shaped element behave like a button. Used instead of
// <button> because the cards hold block content (and sometimes other
// controls), which isn't valid inside a real button.
export const pressable = onActivate => ({
  role: 'button',
  tabIndex: 0,
  onClick: onActivate,
  onKeyDown: e => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onActivate(e);
    }
  }
});

// Image URL for a stored checklist photo.
export const photoUrl = filename =>
  filename
    ? `${process.env.REACT_APP_GLOBAL_SERVER}sproutmanagement/api/images/${filename}`
    : null;
